/**
 * @module types/products
 * @category Type
 */
import { getAllEnumValues } from '@upright/common/utils';
import { ByValueChain, Valence } from '../../types';
import { ModelEntityType } from '../types';
import { ProductTaxonomyDatum } from './legacy/types';
import { IPRAttributes } from '../companies/products/ipr';

export enum TaxonomyProblemType {
  PRE_RUN = 'Pre run',
  POST_RUN = 'Post run',
  WARNING = 'Warning',
}

/**
 * Descriptive (product) phrases by value chain
 */
export type PhrasesByValueChain = ByValueChain<string[]>;

export interface CommonProductDatumProperties {
  lastModifiedBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface EnrichedPhrase extends CommonProductDatumProperties {
  phrase: string;
}

export type EnrichedPhrasesByValueChain = ByValueChain<EnrichedPhrase[]>;

export interface EnrichedRelation extends CommonProductDatumProperties {
  code: string;
  reasoning?: string;
}

export interface EnrichedParentRelation extends CommonProductDatumProperties {
  code: string;
}

export interface EnrichedIsic extends CommonProductDatumProperties {
  code: string;
  type: IsicType;
  weight?: number;
}

export interface ProductLabelMarketSize extends CommonProductDatumProperties {
  value?: number;
  source?: string;
}

export enum CommerceType {
  B2C = 'B2C',
  B2B = 'B2B',
  B2BC = 'B2BC',
}

export const allCommerceTypes: CommerceType[] = [CommerceType.B2C, CommerceType.B2BC, CommerceType.B2B];

export interface ProductLabelCommerce extends CommonProductDatumProperties {
  type: CommerceType;
  ratio?: number; // B2C ratio
  source?: string;
}

export interface SingleIsic {
  code: string;
  type: IsicType;
  weight?: number;
  lastModifiedBy?: string;
  updatedAt?: string;
}

export interface IsicWithSource {
  isic: SingleIsic;
  source: string;
}

export enum TagType {
  DOWNSTREAM = 'downstream',
  UPSTREAM = 'upstream',
  ENABLES = 'enables',
  ENABLED_BY = 'enabled-by',
  BLACKLIST = 'blacklist',
  CRITICAL = 'critical',
}

export enum DBTagType {
  DOWNSTREAM = 'downstream',
  ENABLES = 'enables',
  BLACKLIST = 'blacklist',
  CRITICAL = 'critical',
}

export type ValueChainTag = {
  source: string;
  sourceOrigin: string;
  target: string;
  targetOrigin: string;
  type: DBTagType;
  createdAt: string;
  lastModifiedBy?: string;
};

export const allDbTagTypes: DBTagType[] = getAllEnumValues(DBTagType, 'string');

export function toDbTag(type: TagType): DBTagType {
  switch (type) {
    case TagType.DOWNSTREAM:
    case TagType.UPSTREAM:
      return DBTagType.DOWNSTREAM;
    case TagType.ENABLED_BY:
    case TagType.ENABLES:
      return DBTagType.ENABLES;
    case TagType.BLACKLIST:
      return DBTagType.BLACKLIST;
    case TagType.CRITICAL:
      return DBTagType.CRITICAL;
  }
}

export function toTagType(dbTag: DBTagType): TagType {
  switch (dbTag) {
    case DBTagType.BLACKLIST:
      return TagType.BLACKLIST;
    case DBTagType.CRITICAL:
      return TagType.CRITICAL;
    case DBTagType.DOWNSTREAM:
      return TagType.DOWNSTREAM;
    case DBTagType.ENABLES:
      return TagType.ENABLES;
  }
}

export function isReverseTag(type: TagType): boolean {
  switch (type) {
    case TagType.DOWNSTREAM:
    case TagType.ENABLES:
    case TagType.BLACKLIST:
    case TagType.CRITICAL:
      return false;
    case TagType.UPSTREAM:
    case TagType.ENABLED_BY:
      return true;
  }
}

export function tagOrder(type: DBTagType): number {
  switch (type) {
    case DBTagType.BLACKLIST:
      return 1;
    case DBTagType.CRITICAL:
      return 2;
    case DBTagType.DOWNSTREAM:
      return 3;
    case DBTagType.ENABLES:
      return 4;
  }
}

export type EnrichTaggedRelation = {
  [type in DBTagType]: EnrichedRelation[];
};

/**
 * Comprehensive representation of a product in the product taxonomy
 */
export interface ProductLabelDatum extends CommonProductDatumProperties {
  productCode: string;
  title: string;
  description?: string;
  parents: EnrichedParentRelation[];
  children: EnrichedRelation[];
  leafs?: string[];
  /**
   * These are plainPhrases only
   */
  phrases: EnrichedPhrasesByValueChain;
  incomingTags: EnrichTaggedRelation;
  outgoingTags: EnrichTaggedRelation;
  piaacIsics: EnrichedIsic[];
  sdbsIsics: EnrichedIsic[];
  directWikiQuote: boolean;
  marketSize?: ProductLabelMarketSize;
  marketSizeEstimate?: number;
  commerce?: ProductLabelCommerce;
  wikiLink?: string;
  mappable: boolean;
  floating: boolean;
  phenomenon: boolean;
  mixin: boolean;
  mixinExpression?: string;
  ipra?: Partial<IPRAttributes>;
  ipraHash?: string;
  /**
   * Keys of `ipra` a person set rather than the APG auto-matching them (see
   * `product_mixin.ipraManualFields`). `ipra` is the merged blob, so this is the only record of
   * which of its values were a human choice. No computation reads it today, but it is part of the
   * mixin identity hash, so a manually-set product and an identical auto-matched one keep separate
   * mixins. Absent for mixins baked before it was recorded, and for every non-mixin product.
   */
  ipraManualFields?: string[];
  naturality: number | undefined;
}

export type MixinLabelDatum = Omit<
  ProductLabelDatum,
  'mixinExpression' | 'mixin' | 'floating' | 'phenomenon' | 'naturality'
> & { mixinExpression: string; mixin: true; floating: false; phenomenon: false; naturality: undefined };

export enum RelativeType {
  CHILDREN = 'children',
  PARENTS = 'parents',
}

export interface RelativeCodesByCode {
  [key: string]: string[];
}

/**
 * Product taxonomy
 *
 * Describes
 * - the hierarchy of products
 * - their search phrases
 * - value chain links (TODO)
 */
export interface ProductTaxonomy {
  byCode: {
    [code: string]: ProductTaxonomyDatum;
  };
  childrenByParent: {
    [parent: string]: string[];
  };
}

/**
 * Attributes that are external data to product_labels table. Note that some of these attributes
 * come from same tables ie. (children, parents, leafs) -> product_label_relations
 */
export type ProductAttributeKey =
  | 'children'
  | 'parents'
  | 'leafs'
  | 'phrases'
  | 'incomingTags'
  | 'outgoingTags'
  | 'piaacIsics'
  | 'sdbsIsics'
  | 'marketSize'
  | 'marketSizeEstimate'
  | 'commerce';

export type BasicProductLabelDatum = Omit<ProductLabelDatum, ProductAttributeKey>;

/**
 * Slim per-product datum: basic fields plus the taxonomy graph
 * (`parents`, `children`, `leafs`). What the UI keeps in Redux at
 * `state.data.products` after PLT-4774. Heavy attributes (`phrases`,
 * `incomingTags`, `outgoingTags`, `marketSize`, `commerce`, …) are
 * fetched per-product via `useGetProductData` instead.
 */
export type BasicProductWithGraph = BasicProductLabelDatum & Pick<ProductLabelDatum, 'parents' | 'children' | 'leafs'>;

/**
 * Slim graph product augmented with market-size data. Built in the UI by
 * merging the slim products-by-code (from Redux) with the
 * `GET /api/products/{version}/market-sizes` aggregate response. See
 * PLT-4774 — heavy `marketSize` and `marketSizeEstimate` reads no longer
 * live on the Redux store.
 */
export type ProductWithGraphAndMarketSize = BasicProductWithGraph &
  Pick<ProductLabelDatum, 'marketSize' | 'marketSizeEstimate'>;

export type ProductsByCodeSlimWithMarketSize = ByProductCode<ProductWithGraphAndMarketSize>;

// OA: This is functionally identical to Dictionary<T>, but I find the explicit naming
// helpful when visualizing the shape of data.
export interface ByProductCode<T> {
  [productCode: string]: T;
}
export type ProductsByCode = ByProductCode<ProductLabelDatum>;
/**
 * Slim products-by-code dictionary used by the UI Redux store. See
 * {@link BasicProductWithGraph}.
 */
export type ProductsByCodeSlim = ByProductCode<BasicProductWithGraph>;
export type BasicProductDetailsByCode = ByProductCode<BasicProductData>;
/**
 * Intended use: if you use e.g., the parents and commerce type of a product in a function, type it
 * with ProductAttributesByProductCode<'parents' | 'commerceType'>
 * */
export type ProductAttributesByProductCode<T extends keyof ProductLabelDatum> = ByProductCode<
  Pick<ProductLabelDatum, T>
>;

export type ProductTitleByCode = ProductAttributesByProductCode<'title'>;

export interface RestrictedAccessProduct {
  productCode: string;
  title: string;
  description?: string;
  descriptionSourceUrl?: string;
}

export interface RestrictedAccessProductById {
  [id: string]: RestrictedAccessProduct;
}

export function isIsicType(x: string): x is IsicType {
  return allIsicTypes.indexOf(x as IsicType) > -1;
}

export type IsicType = 'sdbs' | 'piaac';
export const allIsicTypes: IsicType[] = ['sdbs', 'piaac'];
export type IsicNames = {
  [type in IsicType]: {
    [id: string]: string;
  };
};

// ES NOTE: This interface has been used for external purposes
export interface BasicProductData {
  id: string;
  title: string;
  description?: string;
  directWikiQuote: boolean;
  wikiLink?: string;
  commerceType?: CommerceType;
  type: ModelEntityType;
}

export interface TaxonomyProblem {
  text: string;
  relatedProducts: string[];
  type: TaxonomyProblemType;
}

export interface ConstraintProblem {
  status: number;
  error: string;
  isFatalError: boolean;
  impactCode: string;
  constraintType: string;
  valence: Valence;
  type: TaxonomyProblemType;
}

export enum PhenomenonNaturality {
  LOW = 1,
  MEDIUM = 3,
  HIGH = 5,
  SUPER_HIGH = 10,
}

export type ProductStatement = {
  version: string;
  statementId: number;
  productCode: string;
  sourceCode?: string;
  impactTopicId: string;
  score: number;
};

export type ProductStatementCount = {
  productCode: string;
  impactTopicId: string;
  count: number;
};

export type StatementsByCode = ByProductCode<ProductStatement[]>;
